import { useState } from 'react';
import { Badge, Button, Pagination as BronsonPagination, Table } from '@vwfs-bronson/bronson-react';
import { Agent, Job, JOBS_PAGE_SIZE, Pagination, Team } from '../../fes';
import { CompactId } from '../CompactId';
import { openDatadogLogs } from './datadog';
import { formatTotalTokens, formatUsageSummary } from './usage';

const INITIAL_PROMPT_MAX_LENGTH = 64;
const MAX_PAGE_BUTTONS = 5;
const dateFormatter = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

function truncate(value: string | undefined, maxLength: number): string {
  if (!value) {
    return '-';
  }

  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

function formatDateTime(value: string | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
}

function statusModifier(status: string | undefined): string | undefined {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED' || status === 'CANCELLED') return 'error';
  if (status === 'QUEUED' || status === 'STARTING') return 'warning';
  return undefined;
}

function getPageWindow(currentPage: number, totalPages: number, maxButtons: number): number[] {
  const span = Math.min(maxButtons, totalPages);
  let start = currentPage - Math.floor(span / 2);
  start = Math.max(1, Math.min(start, totalPages - span + 1));

  return Array.from({ length: span }, (_, index) => start + index);
}

type JobsTableProps = {
  jobs: Job[];
  agentsById?: Map<string, Agent>;
  teamsById?: Map<string, Team>;
  pagination?: Pagination;
  selectedJobId?: string;
  onSelectJob: (job: Job) => void;
  onPageChange: (offset: number) => Promise<void>;
};

function jobTitle(job: Job): string {
  return job.issueId || job.initialPrompt || job.requestId;
}

function sourceLabel(job: Job): string {
  const channel = job.origin?.channel;

  if (!channel) return 'Unknown';
  if (channel === 'github-pr') return 'GitHub PR';
  if (channel === 'azure-devops') return 'Azure DevOps';
  return channel.charAt(0).toUpperCase() + channel.slice(1);
}

export function JobsTable({
  jobs,
  agentsById = new Map(),
  teamsById = new Map(),
  pagination,
  selectedJobId,
  onSelectJob,
  onPageChange,
}: JobsTableProps) {
  const [isPaginating, setIsPaginating] = useState(false);

  const limit = pagination?.limit ?? JOBS_PAGE_SIZE;
  const offset = pagination?.offset ?? 0;
  const total = pagination?.total ?? jobs.length;
  const totalPages = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
  const currentPage = limit > 0 ? Math.floor(offset / limit) + 1 : 1;

  const goToPage = async (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage || isPaginating) {
      return;
    }

    setIsPaginating(true);
    try {
      await onPageChange((page - 1) * limit);
    } finally {
      setIsPaginating(false);
    }
  };

  if (jobs.length === 0) {
    return <p className="app-empty">No jobs found.</p>;
  }

  return (
    <div className="app-table-wrap">
      <Table className="app-table" colored>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Request</Table.Th>
            <Table.Th>Agent</Table.Th>
            <Table.Th>Team</Table.Th>
            <Table.Th>Source</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Logs</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {jobs.map(job => (
            <Table.Tr
              key={job.requestId}
              aria-selected={job.requestId === selectedJobId}
              className="app-table-row--interactive"
              onClick={() => onSelectJob(job)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectJob(job);
                }
              }}
              selected={job.requestId === selectedJobId}
              tabIndex={0}
            >
              <Table.Td>
                <strong title={jobTitle(job)}>{truncate(jobTitle(job), INITIAL_PROMPT_MAX_LENGTH)}</strong>
                <br />
                <small><CompactId label="request ID" value={job.requestId} /></small>
              </Table.Td>
              <Table.Td>{job.agentId ? agentsById.get(job.agentId)?.name || job.agentId : '-'}</Table.Td>
              <Table.Td>{job.teamId ? teamsById.get(job.teamId)?.name || job.teamId : '-'}</Table.Td>
              <Table.Td>{sourceLabel(job)}</Table.Td>
              <Table.Td>
                {job.status ? <Badge label={job.status} modifier={statusModifier(job.status)} /> : '-'}
              </Table.Td>
              <Table.Td title={job.createdAt}>{formatDateTime(job.createdAt)}</Table.Td>
              <Table.Td>
                <Button
                  ariaLabel={`Open Datadog logs for job ${job.requestId}`}
                  className="app-table-icon-button"
                  hiddenLabel
                  icon="semantic-forward"
                  incognito
                  onClick={event => {
                    event.stopPropagation();
                    openDatadogLogs(job.requestId);
                  }}
                  small
                  title={`Open Datadog logs (${formatUsageSummary(job.usage)}, ${formatTotalTokens(job.usage)} tokens)`}
                >
                  Datadog logs
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {totalPages > 1 ? (
        <div className="app-table-pagination">
          <BronsonPagination
            title="Jobs pagination"
            prevDisabled={isPaginating || currentPage <= 1}
            nextDisabled={isPaginating || currentPage >= totalPages}
            onPrevClick={() => goToPage(currentPage - 1)}
            onNextClick={() => goToPage(currentPage + 1)}
          >
            {getPageWindow(currentPage, totalPages, MAX_PAGE_BUTTONS).map(page => (
              <BronsonPagination.Item
                key={page}
                active={page === currentPage}
                disabled={isPaginating}
                onClick={() => goToPage(page)}
              >
                {page}
              </BronsonPagination.Item>
            ))}
          </BronsonPagination>
        </div>
      ) : null}
    </div>
  );
}
